/**
 * builderApi — the typed client for the authenticated builder endpoints (build-plan §3.6), hand-typed
 * against `BuilderPageController` + `Dtos.kt` (the generated `@aquilixshop/api-client` is still an IAM
 * placeholder). Every call goes through `apiRequest` (Bearer + single-flight refresh + 401 replay).
 *
 * The route (template/handle/locale) is the ONLY thing the builder sends — `store_id` is derived
 * server-side from the JWT and never appears here (build-plan §3.7, §10.1). There is intentionally no
 * `storeId`/`tenant` field anywhere in this module.
 */
import type { BuyBoxJourney, PageDocument, ThemeSettings } from '@aquilixshop/ui-react';
import { type CodConfig, parseCodConfig } from '@aquilixshop/cod-config';
import { apiRequest, errorCode } from './http';
import { ConflictError, HttpError, NoStoreError } from './errors';
import { stripLocalMedia, stripLocalThemeMedia } from './localMedia';

/** Matches `BuilderPageController.getDraft` defaults. */
export interface BuilderRoute {
  template: string;
  handle: string;
  /**
   * Omitted = the STORE's own language, resolved server-side (feature 0050 lot 0). The builder used to
   * pin 'fr' here: a non-French store's pages live in rows of ITS locale, so the builder read a route
   * that does not exist, fell back to the seeded theme, and saved the seller's work into `fr` rows the
   * storefront never serves. Only a deliberate multi-language edit should ever set this.
   */
  locale?: string;
}

export const DEFAULT_ROUTE: BuilderRoute = { template: 'index', handle: 'default' };

/** Mirrors backend `BuilderPageResponse` (Dtos.kt). `document` is the opaque page block tree. */
export interface BuilderPageResponse {
  document: PageDocument;
  version: number;
  publishedAt: string | null;
  hasUnpublishedChanges: boolean;
}

/** Mirrors backend `SaveDraftResponse`. */
export interface SaveDraftResponse {
  version: number;
}

/**
 * Mirrors backend `BuilderTemplatesResponse` (feature 0018). Two shapes on purpose: `handles` is the
 * PUBLISHED-only assignable set, `templates` spans drafts too so the selector can list — and badge — a
 * template the seller just created but has not published yet. `templates` is optional so a builder running
 * against an older backend degrades to the published list instead of rendering an empty menu.
 */
export interface BuilderTemplatesResponse {
  handles: string[];
  templates?: BuilderTemplateEntry[];
}

/** Mirrors backend `BuilderTemplateEntry` — one template with its publish state. */
export interface BuilderTemplateEntry {
  handle: string;
  status: 'DRAFT' | 'PUBLISHED';
  hasUnpublishedChanges: boolean;
}

/** Mirrors backend `CustomPageSummary` (feature 0027 M1) — one row of the builder "Pages" list. */
export interface CustomPageSummary {
  handle: string;
  title: string | null;
  status: 'DRAFT' | 'PUBLISHED';
  hasUnpublishedChanges: boolean;
  updatedAt: string;
}

/** Mirrors backend `CustomPagesResponse`. */
export interface CustomPagesResponse {
  pages: CustomPageSummary[];
}

/** Mirrors backend `PreviewTokenResponse` (feature 0034). `token` is opaque; `expiresAt` is an ISO instant. */
export interface PreviewTokenResponse {
  token: string;
  expiresAt: string;
}

/** Mirrors backend `CreatePageResponse` — the server-generated handle the builder navigates to. */
export interface CreatePageResponse {
  handle: string;
  title: string;
}

const PAGE_PATH = '/api/builder/page';
const PAGES_PATH = '/api/builder/pages';
const TEMPLATES_PATH = '/api/builder/templates';
/** Feature 0019 — the store-scoped GLOBAL theme (NOT a page draft): its own live, versionless row. */
const THEME_PATH = '/api/store/settings/theme';
/** Feature 0026 — the COD app's config (the SAME endpoint the dashboard Form designer reads/writes). */
const COD_CONFIG_PATH = '/api/store/apps/cod/config';
const BUY_BOX_JOURNEYS_PATH = '/api/builder/buy-box-journeys';
/** Feature 0034 — mints the short-lived grant the storefront preview URL carries. */
const PREVIEW_TOKEN_PATH = '/api/builder/preview-token';

/**
 * Feature 0034 — the staged theme of an UNPUBLISHED workspace, a SIBLING of {@link THEME_PATH}. The id is
 * encoded even though the backend re-validates it against its own kebab alphabet (defence in depth: the
 * caller must never be able to shape the path).
 */
function themeWorkspacePath(themeId: string): string {
  return `${THEME_PATH}/workspaces/${encodeURIComponent(themeId)}`;
}

/** A locale-keyed query, with `locale` omitted when it means "the store's own language" (0050 lot 0). */
function localeQuery(
  locale: string | undefined,
  base: Record<string, string> = {},
): Record<string, string> {
  return locale ? { ...base, locale } : { ...base };
}

function routeQuery(route: BuilderRoute): Record<string, string> {
  const q: Record<string, string> = { template: route.template, handle: route.handle };
  // Sending an empty `locale=` would be read as an explicit (invalid) locale, not as "the store's".
  if (route.locale) q.locale = route.locale;
  return q;
}

/**
 * Map a 409 from a builder write/read to the right typed error by its envelope `code` so the store can
 * branch (NO_ACTIVE_STORE → create-store redirect; PAGE_VERSION_CONFLICT → reconcile UI).
 */
function classify(e: unknown): never {
  if (e instanceof HttpError && e.status === 409) {
    const code = errorCode(e.body);
    if (code === 'NO_ACTIVE_STORE') throw new NoStoreError(e.message, e.body);
    if (code === 'PAGE_VERSION_CONFLICT') throw new ConflictError(e.message, e.body);
  }
  throw e;
}

export const builderApi = {
  /**
   * GET /api/builder/page — the draft document + optimistic-lock version + publish metadata. A 404
   * (`PAGE_NOT_FOUND`) means the route is unseeded (caller treats it as an empty canvas; first save
   * creates the draft). A 409 `NO_ACTIVE_STORE` means the seller has no store yet.
   */
  async getDraft(route: BuilderRoute = DEFAULT_ROUTE): Promise<BuilderPageResponse> {
    try {
      return await apiRequest<BuilderPageResponse>(PAGE_PATH, { query: routeQuery(route) });
    } catch (e) {
      return classify(e);
    }
  },

  /**
   * PUT /api/builder/page — save the draft with the optimistic-lock `version`. First save of an
   * unseeded page sends `version: 0`. A stale version → ConflictError (M3.4 reconcile path).
   *
   * Local (`blob:` / `data:`) media is stripped HERE, at the one writer every path goes through —
   * autosave, global chrome, theme workspace, page duplication. Since 2026-09-15 a picked image shows
   * in the canvas from the seller's own disk while it uploads (see `lib/localMedia.ts`), and such a URL
   * is alive only in the tab that minted it: persisted it would be a broken image on reload, published
   * it would be a broken image on a real storefront. Stripping at the choke-point is what makes "a saved
   * document never references this tab" true by construction instead of by everyone remembering.
   */
  async saveDraft(
    route: BuilderRoute,
    document: PageDocument,
    version: number,
  ): Promise<SaveDraftResponse> {
    try {
      return await apiRequest<SaveDraftResponse>(PAGE_PATH, {
        method: 'PUT',
        query: routeQuery(route),
        body: { document: stripLocalMedia(document), version },
      });
    } catch (e) {
      return classify(e);
    }
  },

  /** POST /api/builder/page/publish — publish the current draft snapshot (204). */
  async publish(route: BuilderRoute = DEFAULT_ROUTE): Promise<void> {
    try {
      await apiRequest<void>(`${PAGE_PATH}/publish`, {
        method: 'POST',
        query: routeQuery(route),
      });
    } catch (e) {
      return classify(e);
    }
  },

  /**
   * DELETE /api/builder/page — delete a THEME-WORKSPACE route (spec 0032; the backend refuses any
   * handle without the reserved `theme-ws-` prefix, so live rows are untouchable). Idempotent → 204.
   * Called by publishTheme after a successful theme publish: the workspace BECAME the live theme.
   */
  async deleteWorkspacePage(route: BuilderRoute): Promise<void> {
    try {
      await apiRequest<void>(PAGE_PATH, {
        method: 'DELETE',
        query: routeQuery(route),
      });
    } catch (e) {
      return classify(e);
    }
  },

  /**
   * GET /api/builder/templates — the PUBLISHED named-template handle suffixes for a (template, locale)
   * (feature 0018). Powers the Topbar template selector. Tenant-scoped server-side from the JWT.
   */
  async listTemplates(template: string, locale?: string): Promise<BuilderTemplatesResponse> {
    try {
      return await apiRequest<BuilderTemplatesResponse>(TEMPLATES_PATH, {
        query: localeQuery(locale, { template }),
      });
    } catch (e) {
      return classify(e);
    }
  },

  /**
   * GET /api/builder/pages — the store's custom CMS pages (feature 0027 M1), one entry per handle with
   * title + published/draft status. Tenant-scoped server-side from the JWT.
   */
  async listPages(locale?: string): Promise<CustomPagesResponse> {
    try {
      return await apiRequest<CustomPagesResponse>(PAGES_PATH, { query: localeQuery(locale) });
    } catch (e) {
      return classify(e);
    }
  },

  /**
   * POST /api/builder/pages — create a custom page from a title + a client-generated starter document. The
   * server slugifies the title to a unique handle and returns it (the builder then opens that handle).
   */
  async createPage(
    locale: string | undefined,
    title: string,
    document: PageDocument,
  ): Promise<CreatePageResponse> {
    try {
      return await apiRequest<CreatePageResponse>(PAGES_PATH, {
        method: 'POST',
        query: localeQuery(locale),
        body: { title, document },
      });
    } catch (e) {
      return classify(e);
    }
  },

  /** PATCH /api/builder/pages/{handle} — rename a custom page's title (handle immutable). 204. */
  async renamePage(locale: string | undefined, handle: string, title: string): Promise<void> {
    try {
      await apiRequest<void>(`${PAGES_PATH}/${encodeURIComponent(handle)}`, {
        method: 'PATCH',
        query: localeQuery(locale),
        body: { title },
      });
    } catch (e) {
      return classify(e);
    }
  },

  /** DELETE /api/builder/pages/{handle} — delete a custom page (both DRAFT + PUBLISHED rows). 204. */
  async deletePage(locale: string | undefined, handle: string): Promise<void> {
    try {
      await apiRequest<void>(`${PAGES_PATH}/${encodeURIComponent(handle)}`, {
        method: 'DELETE',
        query: localeQuery(locale),
      });
    } catch (e) {
      return classify(e);
    }
  },

  /**
   * GET /api/store/settings/theme — the store's global theme (feature 0019). Returns the raw stored v2
   * blob (the backend never up-converts — the FRONT is the sole migrator, so the caller runs
   * `migrateThemeSettings`). Tenant-scoped server-side from the JWT; a 409 `NO_ACTIVE_STORE` means the
   * seller has no store yet.
   */
  async getTheme(): Promise<ThemeSettings> {
    try {
      return await apiRequest<ThemeSettings>(THEME_PATH);
    } catch (e) {
      return classify(e);
    }
  },

  /**
   * PUT /api/store/settings/theme — persist the global theme (feature 0019). Live-on-save, versionless
   * (last-write-wins single-writer row) — NO optimistic lock, so it never conflicts with the page draft.
   * Called ONLY by the dedicated `saveTheme` flush, NEVER from `serialize()`/autosave. A 422
   * (InvalidThemeSettings) surfaces as an HttpError; the front already validates before emit.
   */
  async saveTheme(theme: ThemeSettings): Promise<ThemeSettings> {
    try {
      return await apiRequest<ThemeSettings>(THEME_PATH, {
        method: 'PUT',
        // A favicon whose upload has not landed yet is a `blob:` URL alive only in this tab — never wire it.
        body: stripLocalThemeMedia(theme),
      });
    } catch (e) {
      return classify(e);
    }
  },

  /**
   * POST /api/builder/preview-token — mint the short-lived grant that lets the storefront render this
   * handle's DRAFT pages (feature 0034). The store is taken server-side from the JWT and stamped INTO the
   * token, and the handle is checked for shape AND ownership before anything is signed, so this cannot ask
   * for a preview of a page the seller could not already open. A 404 means the handle is not previewable.
   */
  async mintPreviewToken(handle: string, locale?: string): Promise<PreviewTokenResponse> {
    try {
      return await apiRequest<PreviewTokenResponse>(PREVIEW_TOKEN_PATH, {
        method: 'POST',
        // `locale` absent = the store's own language; the backend request DTO takes it nullable.
        body: locale ? { handle, locale } : { handle },
      });
    } catch (e) {
      return classify(e);
    }
  },

  /**
   * GET /api/store/settings/theme/workspaces/{themeId} — the staged colors/typography of an UNPUBLISHED
   * theme (feature 0034 Bloc 1). A 404 is the CONTRACT, not an error: it means the seller has never
   * customized this theme, and the caller seeds from the front's theme registry instead. Same raw v2 blob
   * as `getTheme` (the backend never up-converts).
   */
  async getThemeWorkspace(themeId: string): Promise<ThemeSettings> {
    try {
      return await apiRequest<ThemeSettings>(themeWorkspacePath(themeId));
    } catch (e) {
      return classify(e);
    }
  },

  /**
   * PUT /api/store/settings/theme/workspaces/{themeId} — persist a workspace's staged theme. Writes a
   * SIBLING sub-key, never the live `themeSettings` row, so a seller merely trying a theme out can never
   * repaint their live store. Versionless / last-write-wins like `saveTheme`.
   */
  async saveThemeWorkspace(themeId: string, theme: ThemeSettings): Promise<ThemeSettings> {
    try {
      return await apiRequest<ThemeSettings>(themeWorkspacePath(themeId), {
        method: 'PUT',
        body: stripLocalThemeMedia(theme),
      });
    } catch (e) {
      return classify(e);
    }
  },

  /**
   * DELETE /api/store/settings/theme/workspaces/{themeId} — drop a CONSUMED workspace (publishTheme
   * promoted it to the live row). Idempotent → 204 even when it was already gone.
   */
  async deleteThemeWorkspace(themeId: string): Promise<void> {
    try {
      await apiRequest<void>(themeWorkspacePath(themeId), { method: 'DELETE' });
    } catch (e) {
      return classify(e);
    }
  },

  /**
   * GET /api/store/apps/cod/config — the seller's COD app config (feature 0026). The endpoint returns the
   * raw stored JSON map (or an empty map when the seller never opened the COD app); `parseCodConfig` is
   * defensive and fills every core field + default theme, so the return is always a complete
   * {@link CodConfig}. Tenant-scoped server-side from the JWT — no `store_id` is sent. A 409 `NO_ACTIVE_STORE`
   * is classified like the theme call so the load path routes to the create-store state.
   */
  async getCodConfig(): Promise<CodConfig> {
    try {
      return parseCodConfig(await apiRequest<Record<string, unknown>>(COD_CONFIG_PATH));
    } catch (e) {
      return classify(e);
    }
  },

  /**
   * GET /api/builder/buy-box-journeys — the buy-boxes of every OTHER draft page of the store, with the
   * journey each one stores (05/10/2026). Behind the header's "Show cart" switch: the header is one document
   * per store, so the rule has to see the whole store, and the editor unions this with the page it has open
   * (see `state/cartMode.ts`). `(template, handle, locale)` names the open page to leave out. Entries that
   * are not objects with a string `orderAction` are dropped; a failure is the CALLER's to read as "unknown".
   */
  async getBuyBoxJourneys(route: BuilderRoute = DEFAULT_ROUTE): Promise<BuyBoxJourney[]> {
    try {
      const res = await apiRequest<{ journeys?: unknown }>(BUY_BOX_JOURNEYS_PATH, {
        query: routeQuery(route),
      });
      const raw = Array.isArray(res?.journeys) ? res.journeys : [];
      const out: BuyBoxJourney[] = [];
      for (const item of raw) {
        if (!item || typeof item !== 'object') continue;
        const r = item as Record<string, unknown>;
        if (typeof r['orderAction'] !== 'string') continue;
        out.push({
          template: typeof r['template'] === 'string' ? r['template'] : '',
          handle: typeof r['handle'] === 'string' ? r['handle'] : '',
          orderAction: r['orderAction'],
        });
      }
      return out;
    } catch (e) {
      return classify(e);
    }
  },
};
